/**
 * "Session expired" dialog (see `stores/reauth.ts`): shown instead of the redirect to
 * `/login` when a 401 arrives while there is unsaved work. Signing in again keeps the page
 * and flushes the unsaved work; "Sign out" leaves for the login page (the annotator's
 * pending edits stay in this browser and are offered again when the image is reopened).
 */
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, errorMessage, isApiError, loginPath } from '@/api/client';
import { qk } from '@/api/keys';
import type { Me } from '@/api/types';
import { Button, Field, Input, Modal, toast } from '@/components';
import { flushUnsavedWork, markLeavingIntentionally, unsavedWorkLabels } from '@/lib/unsavedWork';
import styles from '@/pages/forms.module.css';
import { resolveReauth, useReauth } from '@/stores/reauth';

function reloginError(error: unknown): string {
  if (isApiError(error)) {
    if (error.status === 401) return 'Wrong email or password.';
    if (error.status === 429) return 'Too many failed attempts. Wait a few minutes and try again.';
  }
  return errorMessage(error);
}

export function ReloginModal() {
  const open = useReauth((s) => s.open);
  if (!open) return null;
  return <ReloginDialog />;
}

function ReloginDialog() {
  const queryClient = useQueryClient();
  const user = useReauth((s) => s.user);
  const nextPath = useReauth((s) => s.nextPath);
  const [email, setEmail] = useState(user?.email ?? '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const labels = unsavedWorkLabels();

  const signOut = () => {
    markLeavingIntentionally();
    resolveReauth(false);
    window.location.assign(loginPath(nextPath ?? undefined));
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const me = await api.post<Me>(
        '/api/auth/login',
        { email: email.trim(), password },
        { redirectOn401: false },
      );
      if (user && me.id !== user.id) {
        // another account: never save someone else's pending edits under it
        markLeavingIntentionally();
        resolveReauth(false);
        window.location.assign('/');
        return;
      }
      queryClient.setQueryData(qk.auth.me(), me);
      resolveReauth(true);
      await flushUnsavedWork();
      void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
      toast.success('Signed in again', labels.length ? 'Your pending changes were sent.' : undefined);
    } catch (err) {
      setError(reloginError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => undefined}
      dismissible={false}
      size="sm"
      title="Your session has expired"
      description="Sign in again to keep working. Nothing is lost: your unsaved changes stay on this page and are saved once you are signed in."
    >
      <form className={styles.form} onSubmit={(e) => void onSubmit(e)} noValidate>
        {labels.length > 0 && (
          <ul className="small muted" aria-label="Unsaved work">
            {labels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        )}
        {error && (
          <div className={styles.alert} role="alert">
            <AlertCircle aria-hidden />
            <span>{error}</span>
          </div>
        )}
        <Field label="Email">
          <Input
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Password">
          <Input
            type="password"
            name="password"
            autoComplete="current-password"
            autoFocus
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <div className={styles.actions}>
          <Button type="button" variant="ghost" onClick={signOut}>
            Sign out
          </Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!email.trim() || !password}>
            Sign in
          </Button>
        </div>
      </form>
    </Modal>
  );
}
